const OFFLINE_LIBRARY_DB = "fic-reader-offline-library";
const OFFLINE_LIBRARY_STORE = "books";
const OFFLINE_LIBRARY_META_KEY = "fic-reader-offline-meta-v1";

const normalizeOfflineBlob = (value) => {
  if (!value) return null;
  if (value instanceof Blob) return value;
  if (value instanceof ArrayBuffer) {
    return new Blob([value], { type: "application/epub+zip" });
  }
  if (ArrayBuffer.isView(value)) {
    return new Blob([value.buffer], { type: "application/epub+zip" });
  }
  if (value?.buffer instanceof ArrayBuffer) {
    return new Blob([value.buffer], { type: "application/epub+zip" });
  }
  return null;
};

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {}
};

const notifyOfflineLibraryChanged = () => {
  window.dispatchEvent(new Event("offline-library-updated"));
};

const buildBookKey = (userId, epubId) =>
  `${Number(userId) || 0}:${Number(epubId) || 0}`;

const getMetaMap = () => readJson(OFFLINE_LIBRARY_META_KEY, {});

const setMetaMap = (nextMap) => {
  writeJson(OFFLINE_LIBRARY_META_KEY, nextMap);
  notifyOfflineLibraryChanged();
};

const openOfflineLibraryDb = () =>
  new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB is unavailable"));
      return;
    }

    const request = window.indexedDB.open(OFFLINE_LIBRARY_DB, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OFFLINE_LIBRARY_STORE)) {
        db.createObjectStore(OFFLINE_LIBRARY_STORE, { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error || new Error("Failed to open offline library DB"));
  });

const withStore = async (mode, handler) => {
  const db = await openOfflineLibraryDb();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(OFFLINE_LIBRARY_STORE, mode);
    const store = transaction.objectStore(OFFLINE_LIBRARY_STORE);

    let result;
    try {
      result = handler(store);
    } catch (error) {
      db.close();
      reject(error);
      return;
    }

    transaction.oncomplete = () => {
      db.close();
      resolve(result);
    };
    transaction.onerror = () => {
      db.close();
      reject(
        transaction.error || new Error("Offline library transaction failed")
      );
    };
  });
};

export const listOfflineBooks = (userId) => {
  const metaMap = getMetaMap();
  const targetUserId = Number(userId) || 0;

  return Object.values(metaMap)
    .filter((entry) => Number(entry?.userId) === targetUserId)
    .sort(
      (left, right) => Number(right?.savedAt || 0) - Number(left?.savedAt || 0)
    )
    .map((entry) => entry.metadata);
};

export const getOfflineBookMetadata = (userId, epubId) => {
  const metaMap = getMetaMap();
  return metaMap[buildBookKey(userId, epubId)]?.metadata || null;
};

export const isOfflineBookAvailable = (userId, epubId) =>
  Boolean(getOfflineBookMetadata(userId, epubId));

export const cacheOfflineBook = async ({
  userId,
  epubId,
  metadata,
  buffer,
  blob
}) => {
  const key = buildBookKey(userId, epubId);
  const normalizedUserId = Number(userId) || 0;
  const normalizedEpubId = Number(epubId) || 0;
  const savedAt = Date.now();

  const normalizedBlob = normalizeOfflineBlob(blob || buffer);
  if (!normalizedBlob) {
    throw new Error("Unsupported offline book payload");
  }

  await withStore("readwrite", (store) => {
    store.put({
      key,
      userId: normalizedUserId,
      epubId: normalizedEpubId,
      blob: normalizedBlob,
      savedAt
    });
  });

  const metaMap = getMetaMap();
  metaMap[key] = {
    key,
    userId: normalizedUserId,
    epubId: normalizedEpubId,
    savedAt,
    metadata: {
      ...metadata,
      id: normalizedEpubId,
      offline_saved_at: savedAt
    }
  };
  setMetaMap(metaMap);
};

export const getOfflineBookRecord = async (userId, epubId) => {
  const key = buildBookKey(userId, epubId);
  const metadata = getOfflineBookMetadata(userId, epubId);

  try {
    const record = await withStore("readonly", (store) => {
      const request = store.get(key);
      return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () =>
          reject(request.error || new Error("Failed to read offline book"));
      });
    });

    if (!record && !metadata) return null;
    const normalizedBlob = normalizeOfflineBlob(record?.blob || record?.buffer || null);
    return {
      ...(record || {}),
      blob: normalizedBlob,
      metadata
    };
  } catch (error) {
    if (!metadata) return null;
    return { key, metadata, blob: null, buffer: null };
  }
};

export const removeOfflineBook = async (userId, epubId) => {
  const key = buildBookKey(userId, epubId);

  try {
    await withStore("readwrite", (store) => {
      store.delete(key);
    });
  } catch (error) {}

  const metaMap = getMetaMap();
  delete metaMap[key];
  setMetaMap(metaMap);
};

export const updateOfflineBookMetadata = (userId, epubId, updates) => {
  const key = buildBookKey(userId, epubId);
  const metaMap = getMetaMap();
  const entry = metaMap[key];
  if (!entry?.metadata) return null;

  const nextMetadata = {
    ...entry.metadata,
    ...updates
  };
  metaMap[key] = {
    ...entry,
    metadata: nextMetadata,
    savedAt: Date.now()
  };
  setMetaMap(metaMap);
  return nextMetadata;
};

export { notifyOfflineLibraryChanged };
